import { memo, useCallback, useMemo } from 'react';
import { isString } from 'radash';
import { getFitFillDisabledReasonForScene, isFitAppliedForScene } from '../../../../clipUpdateDataUtils/sceneUtils';
import { LayoutType } from '@/App/remotion/MainPlayer/utils/speakerLayoutGenerator/types';
import { FitType } from '@/domains/asset';
import {
  DEFAULT_LAYOUT_OPTION,
  SCENE_LAYOUT_TYPES
} from '@/App/remotion/MainPlayer/utils/speakerLayoutGenerator/playground/options';
import Icon from '@/components/atoms/Icon';
import Button from '@/components/atoms/Button/Button';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { isCustomUploadClip, isOriginalLayoutAvailable } from '@/libs/clipUtils';

import { classnames } from '@/libs/utils';
import useClipCustomiserLayoutPopup from '@/Pages/Clip/ClipPlayer/ClipCustomizerV2/Layout/useClipLayoutPopupOptions';
import Toggle from '@/components/atoms/Toggle';
import TooltipV2 from '@/components/TooltipV2';
import { Scenes } from '@/App/remotion/MainPlayer/types/Scene';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { LayoutDisableReason } from '@/Pages/Clip/ClipPlayer/ClipCustomizer/constants';
import { doesClipWorkOnAlignedStreams, isSplitsInTimelineEnabled } from '@/stores/clip';

interface SceneOptionsLayoutSubMenuProps {
  onSelect: (details: { layout: LayoutType; fitMode: FitType; updateAllScenes?: boolean }) => void;
  scene: Scenes[0];
}

const SceneOptionsLayoutSubMenu = ({ onSelect, scene }: SceneOptionsLayoutSubMenuProps) => {
  const currentLayout = useMemo(() => scene.details.layout, [scene.details.layout]);
  const currentFitMode = useMemo(() => scene.details.fitMode, [scene.details.fitMode]);
  const { clipData } = useClipsContext();
  const splitsEnabled = isSplitsInTimelineEnabled(clipData);

  const {
    originalLayoutDisableReason,
    gridDisableReason,
    focusDisableReason,
    pipDisableReason,
    fullScreenDisableReason
  } = useClipCustomiserLayoutPopup();

  const layoutDisabledMap: Record<LayoutType, string | null> = useMemo(() => {
    return {
      DEFAULT: originalLayoutDisableReason,
      SPEAKER: fullScreenDisableReason,
      GRID: gridDisableReason,
      FOCUS: focusDisableReason,
      PIP: pipDisableReason,
      AUDIOGRAM: null
    };
  }, [originalLayoutDisableReason, fullScreenDisableReason, gridDisableReason, focusDisableReason, pipDisableReason]);

  const handleFitModeChange = useCallback(
    (fitMode: FitType) => {
      onSelect({ layout: currentLayout, fitMode });
    },
    [onSelect, currentLayout]
  );

  const isAudiogramScene = useMemo(() => {
    return scene.details.layout === 'AUDIOGRAM';
  }, [scene.details.layout]);

  const handleLayoutChange = useCallback(
    (layout: LayoutType) => {
      onSelect({ layout, fitMode: currentFitMode, updateAllScenes: splitsEnabled && isAudiogramScene });
    },
    [onSelect, currentFitMode]
  );

  const sceneLayoutOptions = useMemo(() => {
    const sceneLayouts: (typeof DEFAULT_LAYOUT_OPTION)[] = [];
    if (doesClipWorkOnAlignedStreams(clipData) || isCustomUploadClip({ clip: clipData })) {
      sceneLayouts.push(...SCENE_LAYOUT_TYPES);
    }
    if (isOriginalLayoutAvailable(clipData)) {
      sceneLayouts.unshift(DEFAULT_LAYOUT_OPTION);
    }
    return sceneLayouts;
  }, [clipData]);

  const isFitApplied = useMemo(() => {
    return isFitAppliedForScene({ clip: clipData, scene });
  }, [scene, clipData]);
  const fitfillDisabledReason = useMemo(() => {
    return getFitFillDisabledReasonForScene({ clip: clipData, scene });
  }, [scene, clipData]);

  const isFitFillDisabled = useMemo(() => {
    return isString(fitfillDisabledReason);
  }, [fitfillDisabledReason]);

  return (
    <div className="p-3" onClick={e => e.stopPropagation()}>
      <div className="font-medium">Layout</div>
      {splitsEnabled && isAudiogramScene && (
        <div className="mt-5 rounded-md border border-black border-opacity-10 bg-orange-50 px-3 py-2.5">
          <div className="text-sm font-medium">Audiogram layout applied</div>
          <div className="mt-1 text-xs font-light">
            Audiogram can't be mixed with other scene types. Applying a scene change will override all scenes in all
            splits.
          </div>
        </div>
      )}
      <div className="mt-3 flex flex-col gap-1.5">
        {sceneLayoutOptions.map(({ label, value, icon }) => {
          const isActive = currentLayout === value;
          const isDisabled = isString(layoutDisabledMap[value]);
          const disabledReason = layoutDisabledMap[value];

          const disabledClickHandler = () => {
            if (disabledReason === LayoutDisableReason.UNMIGRATED_LIVE_EVENT_DOESNT_SUPPORT_SPEAKER_LAYOUTS) {
              EventBus.dispatch(CustomEvents.OpenEventMigrationPopup, { source: 'SceneLayout' });
            }
          };

          const button = (
            <Button
              trackingId={`scene-options-menu-layout-${value}`}
              key={value}
              className={classnames(
                'flex w-full items-center rounded-md border-none p-2',
                isActive ? 'bg-slate-200' : 'bg-white',
                isDisabled ? 'opacity-30' : ''
              )}
              onClick={isDisabled ? disabledClickHandler : () => handleLayoutChange(value)}
              variation="none"
            >
              <Icon name={icon} className="mr-4 size-5" />
              <span className="flex-1 text-left">{label}</span>
              {isActive && (
                <svg
                  className="ml-auto size-5 text-slate-700"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </Button>
          );
          return (
            <TooltipV2
              key={`scene-options-menu-layout-${value}-tooltip`}
              tooltipElement={button}
              tooltipContent={disabledReason || ''}
              tooltipId={`scene-options-menu-layout-${value}`}
              place={'right'}
              size="base"
              colorTheme="light"
              className="!px-3 !py-1.5 !text-sm"
              opacity={1}
              offset={16}
            />
          );
        })}
      </div>

      <div className="mt-3 font-medium">Options</div>
      <TooltipV2
        key={`scene-options-menu-layout-fit-fill-disabled-reason-tooltip`}
        tooltipElement={
          <div
            className={classnames('mt-3 flex items-center space-x-2', isFitFillDisabled ? 'cursor-not-allowed' : '')}
          >
            <Toggle
              size="small"
              on={isFitApplied}
              onToggle={() => handleFitModeChange(isFitApplied ? 'FILL' : 'FIT')}
              disabled={isFitFillDisabled}
            />
            <div className="text-sm font-medium">Keep aspect ratio of speaker(s)</div>
          </div>
        }
        tooltipContent={fitfillDisabledReason ?? ''}
        tooltipId={`scene-options-menu-layout-fit-fill-disabled-reason`}
        place={'right'}
        size="base"
        colorTheme="light"
        className="!px-3 !py-1.5 !text-sm"
        opacity={1}
        offset={32}
      />
    </div>
  );
};

export default memo(SceneOptionsLayoutSubMenu);
